import { useJobPostingContext } from '@/contexts/job-posting-context';
import { cn } from '@/utils/classnames';

import { BackButton } from './back-button';
import { CampaignDetails } from './campaign-details';
import { STEP_CONTENT_WIDTH } from './header/step-config';
import { JobDetails } from './job-details';
import { OpeningsPanel } from './openings-panel';
import { PoolSetup } from './pool-setup';
import { PostingResult } from './posting-result';
import { SelectChannels } from './select-channels';
import { SelectOpenings } from './select-openings';
import { Summary } from './summary';

import type { JobPostingStep } from './types';

/**
 * Steps that render the right-side `SelectedOpeningsPanel` alongside the main
 * step content. Other steps (`summary`, `result`) get a single-column layout.
 */
const STEPS_WITH_OPENINGS_PANEL = new Set<JobPostingStep>([
  'select-openings',
  'select-channels',
  'job-details',
  'pool-setup',
  'campaign-details',
]);

export function JobPostingFlow() {
  const { currentStep } = useJobPostingContext();
  const showOpeningsPanel = STEPS_WITH_OPENINGS_PANEL.has(currentStep);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex w-full px-4 lg:gap-4">
        {/* Step content column — owns the vertical scroll for most steps;
           select-channels handles its own scroll internally. */}
        <div
          className={cn(
            'flex w-full min-w-0 flex-col items-center gap-3 pb-6 lg:flex-1',
            currentStep === 'select-channels'
              ? 'overflow-hidden'
              : 'overflow-y-auto',
          )}
        >
          <div
            className={cn(
              'flex w-full flex-col gap-3',
              STEP_CONTENT_WIDTH[currentStep],
              currentStep === 'select-channels' && 'min-h-0 flex-1',
            )}
          >
            {currentStep !== 'select-openings' &&
              currentStep !== 'select-channels' &&
              currentStep !== 'result' && <BackButton />}
            {currentStep === 'select-openings' && <SelectOpenings />}
            {currentStep === 'select-channels' && <SelectChannels />}
            {currentStep === 'job-details' && <JobDetails />}
            {currentStep === 'pool-setup' && <PoolSetup />}
            {currentStep === 'campaign-details' && <CampaignDetails />}
            {currentStep === 'summary' && <Summary />}
            {currentStep === 'result' && <PostingResult />}
          </div>
        </div>

        {/* Right rail — stays fixed because it's outside the scroll column. */}
        {showOpeningsPanel && <OpeningsPanel className="hidden lg:flex" />}
      </div>
    </div>
  );
}
